/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * Restore Safety tab — read-only preflight + dry-run only. There is
 * NO live restore / apply / import action here by design (the backend
 * does not expose one). Self-contained: owns its own loading/result
 * state and resets when `backupId` changes.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { AlertTriangle } from "lucide-react";
import { restorePreflight, restoreDryRun } from "./backupApi";
import { JsonBlock } from "./backupFormatters";

export default function BackupRestoreSafetyTab({ backupId }) {
  const [state, setState] = useState({});

  // Reset when targeting a different archive (V1 behaviour).
  useEffect(() => {
    setState({});
  }, [backupId]);

  const runAction = async (kind) => {
    if (!backupId) return;
    const body =
      kind === "preflight"
        ? { backup_id: backupId, target_mode: "dry_run_only", include_uploads_check: true }
        : { backup_id: backupId, target_database_mode: "validate_only", include_uploads: false };
    setState((s) => ({ ...s, [`${kind}_loading`]: true }));
    try {
      const fn = kind === "preflight" ? restorePreflight : restoreDryRun;
      const { data } = await fn(body);
      setState((s) => ({ ...s, [kind]: data, [`${kind}_loading`]: false }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Unknown error";
      setState((s) => ({
        ...s,
        [kind]: {
          __error: String(typeof msg === "object" ? JSON.stringify(msg) : msg),
          __status: e?.response?.status,
        },
        [`${kind}_loading`]: false,
      }));
    }
  };

  return (
    <div className="mt-3 space-y-3" data-testid="restore-tab">
      <div className="text-xs text-amber-400 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5" />
        Live restore is not available in this build. Only preflight and dry-run are enabled —
        neither modifies any data.
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => runAction("preflight")}
          disabled={!!state.preflight_loading}
          data-testid="restore-preflight-btn"
        >
          {state.preflight_loading ? "Running…" : "Run preflight"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => runAction("dryrun")}
          disabled={!!state.dryrun_loading}
          data-testid="restore-dryrun-btn"
        >
          {state.dryrun_loading ? "Running…" : "Run dry-run"}
        </Button>
      </div>
      {state.preflight && (
        <div>
          <div className="text-xs font-semibold text-zinc-300 mb-1">Preflight result</div>
          <JsonBlock data={state.preflight} copyTestId="copy-json-restore-preflight" />
        </div>
      )}
      {state.dryrun && (
        <div>
          <div className="text-xs font-semibold text-zinc-300 mb-1">Dry-run result</div>
          <JsonBlock data={state.dryrun} copyTestId="copy-json-restore-dryrun" />
        </div>
      )}
    </div>
  );
}
